import { AnalyticsEventName } from '@dupe/common/constants'
import { Database } from 'lucide-react'
import { useEffect, useState } from 'react'
import Button from '~/src/components/Button'
import OnboardingTitle from '~/src/components/onboarding/OnboardingTitle'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { Slider } from '~/src/components/ui/slider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useUpdateUserMetadata from '~/src/hooks/useUpdateUserMetadata'
import {
  OnboardingStep,
  useOnboardingActions,
  useOnboardingData,
  useOnboardingStore,
} from '~/src/stores/useOnboardingStore'

const qualityVsValueOptions = [
  { key: 'save-every-dollar', value: 'Save every dollar' },
  {
    key: 'good-mix-of-quality-and-value',
    value: 'A good mix of quality & value',
  },
  { key: 'only-invest-in-quality', value: 'Only invest in quality' },
]

const defaultValue = 50

const QualityVsValueStep: React.FC = () => {
  const analytics = useAnalytics()
  const storedQualityVsValue = useOnboardingStore(
    (state) => state.data.onboarding_data?.qualityVsValue
  )

  const [processing, setProcessing] = useState(false)
  const [value, setValue] = useState<number>(
    storedQualityVsValue ?? defaultValue
  )

  const nextStep = useOnboardingActions().nextStep
  const markStepComplete = useOnboardingActions().markStepComplete
  const setQualityVsValue = useOnboardingActions().setQualityVsValue
  const onboardingData = useOnboardingData()

  const { mutateAsync: updateUserMetadata } = useUpdateUserMetadata()

  const handleContinue = async () => {
    if (processing) return

    setProcessing(true)

    // Track analytics
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'quality-vs-value',
      value,
    })

    // Save to store
    setQualityVsValue(value)

    // Save to database
    try {
      await updateUserMetadata({
        key: 'onboarding_data',
        value: {
          ...onboardingData,
          qualityVsValue: value,
        },
      })
    } catch (error) {
      console.error('Failed to update quality vs value preference:', error)
    }

    setProcessing(false)

    markStepComplete(OnboardingStep.QUALITY_VS_VALUE)
    nextStep(OnboardingStep.DUPE_VALUE_PROP_ONE)
  }

  // Update value if store data changes
  useEffect(() => {
    if (storedQualityVsValue !== null && storedQualityVsValue !== undefined) {
      setValue(storedQualityVsValue)
    }
  }, [storedQualityVsValue])

  return (
    <>
      <div className='space-y-16 md:space-y-24'>
        <OnboardingTitle
          title='What do you value more when you typically buy furniture?'
          description='This helps us understand your preferences'
        />

        <div className='space-y-6'>
          <div className='grid grid-cols-3 justify-end gap-2'>
            {qualityVsValueOptions.map((option, index) => (
              <div
                key={option.key}
                className='justify-self-center first:justify-self-start last:justify-self-end'
              >
                {index === 0 && (
                  <>
                    <Database className='relative top-2 h-5 w-5' />
                  </>
                )}

                {index === 1 && (
                  <div className='relative'>
                    <Database className='h-5 w-5' />
                    <Database className='dark:bg-darkbg absolute left-2 top-2 h-5 w-5 bg-white' />
                  </div>
                )}

                {index === 2 && (
                  <div className='relative'>
                    <Database className='dark:bg-darkbg absolute left-2 top-0 h-5 w-5 bg-white' />
                    <Database className='dark:bg-darkbg absolute -left-2 top-0 h-5 w-5 bg-white' />
                    <Database className='dark:bg-darkbg relative top-2 h-5 w-5 bg-white' />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className='space-y-4'>
            <Slider
              value={[value]}
              defaultValue={[defaultValue]}
              max={100}
              onValueChange={(newValue) => {
                setValue(newValue?.[0] ?? defaultValue)
              }}
            />
            <div className='grid grid-cols-3 justify-end gap-2'>
              {qualityVsValueOptions.map((option) => (
                <div
                  key={option.key}
                  className='text-center text-sm font-medium first:text-left last:text-right md:text-base'
                >
                  <span className='inline-flex max-w-[100px] md:max-w-[120px]'>
                    {option.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button
          disabled={processing}
          onClick={() => handleContinue()}
          fullWidth
        >
          {processing ? (
            <div className='animate-spin'>
              <LoaderIcon height={34} />
            </div>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </>
  )
}

export default QualityVsValueStep
